/**
 * Debounces the passed in function, causing it to execute once per animation frame.
 * @param fn The function to debounce.
 * @returns A debounced version of the passed in function.
 */
export function debounce<T extends Function>(fn: T): T;

/**
 * Debounces the passed in function, causing it to execute after a specified delay in milliseconds.
 * @param fn The function to debounce.
 * @param delay The number of milliseconds to delay execution of the function by.
 * @returns A debounced version of the passed in function.
 */
export function debounce<T extends Function>(fn: T, delay: number): T;

/**
 * Debounces the passed in function, causing it to execute once per animation frame.
 * @param fn The function to debounce.
 * @param delay The number of milliseconds to delay execution of the function by.
 * @returns A debounced version of the passed in function.
 */
export function debounce<T extends Function>(fn: T, delay?: number): T {
  if (delay !== undefined) {
    let timer: ReturnType<typeof setTimeout> | null = null;

    return ((...args: unknown[]): void => {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }

      timer = setTimeout(() => {
        timer = null;
        fn(...args);
      }, delay);
    }) as unknown as T;
  } else {
    let animationFrame: number | null = null;

    return ((...args: unknown[]): void => {
      if (animationFrame !== null) {
        cancelAnimationFrame(animationFrame);
        animationFrame = null;
      }

      animationFrame = requestAnimationFrame(() => {
        animationFrame = null;
        fn(...args);
      });
    }) as unknown as T;
  }
}
